import type { SkipSegment, SkippedInSession } from './skip-segment.h'

import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { button, span } from '|>/shared/h'
import { Intent } from '|>/shared/types/intent'
import { on, register } from '|>/shared/vjs'
import { BaseComponent } from '../base'
import {
  getActiveSegment,
  getSegmentLabelKey,
  getSegmentSeekTimeMs,
} from './skip-segment-utils'

import './skip-segment-button.css'

/**
 * Skip Intro / Skip Credits button
 * Shows/hides by timecodes and emits event on click
 */
@register
export class SkipSegmentButton extends BaseComponent {
  declare buttonEl?: HTMLElement
  declare labelEl?: HTMLElement

  private segments: SkipSegment[] = []
  private skippedInSession: SkippedInSession = {}
  private activeSegment?: SkipSegment

  constructor(player, options) {
    super(player, options)
    this.hide()
  }

  @on(Events.Media.SkipSegmentsChanged)
  handleSkipSegmentsChanged(segments: Events.Media.SkipSegmentsChanged) {
    this.segments = segments ?? []
    this.skippedInSession = {}
    this.activeSegment = undefined
    this.hide()
  }

  @on(Events.Media.ContentChanged)
  handleContentChanged() {
    this.skippedInSession = {}
    this.activeSegment = undefined
    this.hide()
  }

  @on(Events.Media.AdStateChanged)
  handleAdStateChanged() {
    this.updateVisibility(this.player_.currentTime?.() ?? 0)
  }

  @on(Events.Media.TimeUpdate)
  handleTimeUpdate(currentTime: number) {
    this.updateVisibility(currentTime)
  }

  @on(['seeking', 'seeked'])
  handleSeekWhilePaused() {
    if (!this.player_.paused?.()) return

    this.updateVisibility(this.player_.currentTime?.() ?? 0)
  }

  override createEl() {
    const el = super.createEl()

    this.buttonEl = button('.vjs-x-skip-segment-button', {}, [
      (this.labelEl = span('.vjs-x-skip-segment-button-text', '')),
    ])

    if (this.buttonEl) {
      videojs.on(this.buttonEl, 'click', (e: MouseEvent) => {
        this.handleClick(e)
      })
      el.appendChild(this.buttonEl)
    }

    return el
  }

  override dispose() {
    this.segments = []
    this.skippedInSession = {}
    this.activeSegment = undefined
    super.dispose()
  }

  private updateVisibility(currentTimeSec: number) {
    const durationMs = (this.player_.duration?.() ?? 0) * 1000
    const currentTimeMs = currentTimeSec * 1000
    const isAdPlaying = this.player_.hasClass('vjs-ad-playing')

    const activeSegment = getActiveSegment(
      this.segments,
      currentTimeMs,
      durationMs,
      this.skippedInSession,
      isAdPlaying
    )

    if (!activeSegment) {
      this.activeSegment = undefined
      this.hide()
      return
    }

    this.activeSegment = activeSegment

    if (this.labelEl) {
      this.labelEl.textContent = this.localize(
        getSegmentLabelKey(activeSegment.type)
      )
    }

    this.show()
  }

  handleClick(e: MouseEvent) {
    e?.preventDefault()
    e?.stopPropagation()

    const segment = this.activeSegment
    if (!segment) return

    this.skippedInSession[segment.type] = true
    if (segment.type === 'NEXT_EPISODE') {
      this.skippedInSession.CREDITS = true
    }
    if (segment.type === 'CREDITS') {
      this.skippedInSession.NEXT_EPISODE = true
    }

    this.activeSegment = undefined
    this.hide()

    if (segment.type === 'NEXT_EPISODE') {
      this.player_.trigger(Events.User.Intent, Intent.PlayNextEpisode)
      this.player_.trigger(Events.Player.PlayNextEpisode)
      return
    }

    this.player_.trigger(
      segment.type === 'INTRO'
        ? Events.Player.SkipIntro
        : Events.Player.SkipCredits
    )
    const durationMs = (this.player_.duration?.() ?? 0) * 1000
    const seekMs = getSegmentSeekTimeMs(segment.segmentEndTime, durationMs)
    this.player_.currentTime(seekMs / 1000)
  }
}

SkipSegmentButton.options = {
  className: 'vjs-x-skip-segment vjs-control',
}
